import { useContext, useEffect } from 'react';
import { ga4sendEvent, moEngageUserEventTrack, getFBEventId } from '../user-analytics.service.js';
import { AppContext } from '../../app-context.js';
import { useRouter } from 'next/router';
import { getVwoABTestVariationsEventData } from '../../methods/getABTestVariations.js';
import { useCsDashboard } from '../../hooks/useCsDashboard.js';

var WebsiteUserType;
(function (WebsiteUserType) {
    WebsiteUserType["ADMIN"] = "Admin";
    WebsiteUserType["CLIENT"] = "Client";
    WebsiteUserType["TSO"] = "tso";
})(WebsiteUserType || (WebsiteUserType = {}));
function getDevice() {
    var _a;
    const userAgent = (_a = window === null || window === void 0 ? void 0 : window.navigator) === null || _a === void 0 ? void 0 : _a.userAgent;
    if (/android/i.test(userAgent)) {
        return "Mobile (Android)";
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return "Mobile (iOS)";
    }
    if (/windows phone/i.test(userAgent)) {
        return "Mobile (Windows Phone)";
    }
    return "Desktop";
}
function useEventTrackerService() {
    const router = useRouter();
    const { globalData = {}, setGlobalData } = useContext(AppContext);
    const { utm = {} } = globalData;
    const { utm_source, utm_medium, utm_campaign } = router.query;
    const { isTso } = useCsDashboard();
    const utmFromQuery = {
        utm_source,
        utm_medium,
        utm_campaign
    };
    useEffect(() => {
        setGlobalData === null || setGlobalData === void 0 ? void 0 : setGlobalData(Object.assign(Object.assign({}, globalData), { utm: utmFromQuery }));
    }, []);
    const { name, userId, companyId, serviceType, companyLocation, customerName, customerCompanyId, customerCompanyLocation, customerServiceType, customerPhone, phoneNum, isCsDashboardUser, abTestVariations, thirdPartyCompanyName } = useContext(AppContext);
    const NOT_APPLICABLE = "#N/A";
    const sendGAandMoEngageEvent = (eventData, trackGAEvent = true, trackMoengageEvent = true, trackFbCapiEvent = false) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        let data = Object.assign(Object.assign(Object.assign(Object.assign({ event_source: "web", user_id: userId !== null && userId !== void 0 ? userId : NOT_APPLICABLE, company_id: (_a = customerCompanyId !== null && customerCompanyId !== void 0 ? customerCompanyId : companyId) !== null && _a !== void 0 ? _a : NOT_APPLICABLE, company_location: (_b = customerCompanyLocation !== null && customerCompanyLocation !== void 0 ? customerCompanyLocation : companyLocation) !== null && _b !== void 0 ? _b : NOT_APPLICABLE, services: (_c = customerServiceType !== null && customerServiceType !== void 0 ? customerServiceType : serviceType) !== null && _c !== void 0 ? _c : NOT_APPLICABLE, user_type: isCsDashboardUser ? "Admin" : "Client", third_party_company_name: thirdPartyCompanyName !== null && thirdPartyCompanyName !== void 0 ? thirdPartyCompanyName : NOT_APPLICABLE, web_device: getDevice() }, (isTso && {
            user_type: "tso"
        })), getVwoABTestVariationsEventData(abTestVariations)), utm), eventData.data);
        if (trackGAEvent) {
            let keysToDelete = [
                ...[
                    "is_premium",
                    "is_insurance_activated",
                    "load_type",
                    "from_location",
                    "from_location_id",
                    "from_location_city_id",
                    "from_location_state_id",
                    "to_location",
                    "to_location_id",
                    "to_location_city_id",
                    "to_location_state_id",
                    "material",
                    "quantity",
                    "quantity_type",
                    "required_vehicle_type",
                    "required_body_type",
                    "required_tyres",
                    "loading_time",
                    "recommended_price",
                    "expected_price",
                    "expected_price_type",
                    "payment_type",
                    "advance_percentage",
                    "odc",
                    "odc_length",
                    "odc_breadth",
                    "odc_height"
                ],
                ...[
                    "vehicle_number",
                    "current_location",
                    "current_location_id",
                    "current_location_city_id",
                    "current_location_state_id",
                    "routes",
                    "total_route",
                    "lorry_type",
                    "lorry_capacity",
                    "capacity_type",
                    "total_tyres",
                    "body_type",
                    "vehicle_size",
                    "vehicle_size_type"
                ],
                ...["bid_amount", "submitted_by", "bid_type", "amount", "sent_bid_amount"],
                ...[
                    "profile_user_id",
                    "profile_user_name",
                    "profile_company_id",
                    "profile_company_name",
                    "profile_location",
                    "profile_service_type",
                    "profile_connections",
                    "profile_phone_number",
                    "total_connections"
                ]
            ];
            const dataCloned = Object.assign({}, data);
            keysToDelete.forEach((key) => delete dataCloned[key]);
            ga4sendEvent(eventData.name, Object.assign({}, dataCloned));
        }
        if (trackMoengageEvent) {
            moEngageUserEventTrack(eventData.name, Object.assign({ user_name: NOT_APPLICABLE, company_name: (_d = customerName !== null && customerName !== void 0 ? customerName : name) !== null && _d !== void 0 ? _d : NOT_APPLICABLE, phone_number: (_e = customerPhone !== null && customerPhone !== void 0 ? customerPhone : phoneNum) !== null && _e !== void 0 ? _e : NOT_APPLICABLE }, data));
        }
        if (trackFbCapiEvent) {
            const resolvedCompanyId = customerCompanyId !== null && customerCompanyId !== void 0 ? customerCompanyId : companyId;
            const eventID = getFBEventId((_g = (_f = eventData.data) === null || _f === void 0 ? void 0 : _f.load_id) !== null && _g !== void 0 ? _g : eventData.name, resolvedCompanyId);
            // Keep Meta payloads small so Pixel `eid` isn't dropped on long GET requests.
            // Full `data` is for GA/MoEngage only — passing it to fbq breaks deduplication.
            const metaCustomData = {};
            if (((_h = eventData.data) === null || _h === void 0 ? void 0 : _h.load_id) != null) {
                metaCustomData.load_id = eventData.data.load_id;
            }
            if (((_j = eventData.data) === null || _j === void 0 ? void 0 : _j.bid_id) != null) {
                metaCustomData.bid_id = eventData.data.bid_id;
            }
            (_k = window.fbq) === null || _k === void 0 ? void 0 : _k.call(window, "trackCustom", eventData.name, metaCustomData, { eventID });
            // void sendFbCapiEvent({
            //     event_id: eventID,
            //     event_name: eventData.name,
            //     phone_num: phone,
            //     custom_data: metaCustomData
            // });
        }
    };
    return { sendGAandMoEngageEvent };
}

export { WebsiteUserType, useEventTrackerService };
//# sourceMappingURL=useEventTrackerService.js.map
